import { useKeycloak } from '@react-keycloak/web';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { useHistory, useLocation } from 'react-router';
import { CurrentCompanyContext } from '../hooks/CurrentCompanyContext';
import { ROUTES } from '../routes/routes';
import { UserPlan } from '../utils/plans';
import Modal, { MODAL_STYLES } from './Modal';
import { CreateButton, NavItems, useNavigateTo } from './Sidebar';
import SnippetBox from './SnippetBox';
import { Menu } from '@mantine/core';

interface Props {
    changelog?: boolean;
    currentPlan?: UserPlan;
    showUpgradePopup: () => void;
}

export default function PageHeader({ changelog = false, currentPlan, showUpgradePopup }: Props) {
    const { pathname } = useLocation();
    const history = useHistory();
    const company = useContext(CurrentCompanyContext);
    const navigateTo = useNavigateTo();
    const { keycloak } = useKeycloak();

    useEffect(() => {
        if (!changelog || window.Headway) {
            return;
        }
        window.HW_config = {
            selector: '.changelog',
            account: 'yEKq17',
        };
        const headwayScript = document.createElement('script');
        headwayScript.src = 'https://cdn.headwayapp.co/widget.js';
        headwayScript.type = 'text/javascript';
        headwayScript.async = true;
        document.head.appendChild(headwayScript);
    }, [changelog]);
    const [showEmail, setShowEmail] = useState(true);
    const hideEmail = () => setShowEmail(false);
    const [isOpen, setIsOpen] = useState(false);
    const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
    const handleShowApiKey = () => {
        setIsOpen(false);
        setApiKeyModalOpen(true);
    };

    const handleHideApiKey = () => {
        setApiKeyModalOpen(false);
    };
    const handleClickMenu = () => {
        setIsOpen(!isOpen);
    };

    const handleManageSubscription = () => {
        setIsOpen(false);
        history.push(ROUTES.BILLING);
    };

    const handleSettings = () => {
        setIsOpen(false);
        history.push(ROUTES.SETTINGS);
    };

    const handleLogout = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            keycloak?.logout();
        },
        [keycloak]
    );

    const isAdmin = useMemo(() => (keycloak ? keycloak.hasRealmRole('admin') : false), [keycloak]);

    return (
        <>
            {!!keycloak?.authenticated && (
                <>
                    <header className="w-full flex items-center bg-white py-2 px-6 hidden sm:flex">
                        <div className="w-1/2 text-sm">
                            {showEmail ? (keycloak.tokenParsed as any)?.email : null}{' '}
                            {isAdmin && showEmail && (
                                <button onClick={hideEmail} className="link">
                                    [-]
                                </button>
                            )}
                        </div>
                        <div className="relative w-1/2 flex justify-end">
                            <div className="changelog"></div>

                            <Menu withinPortal>
                                <Menu.Target>
                                    <button onClick={handleClickMenu} className="relative z-10 w-6 h-6 overflow-hidden">
                                        <svg
                                            className="fill-current text-gray-400 hover:text-gray-600"
                                            viewBox="0 0 1792 1792"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M1088 1248v192q0 40-28 68t-68 28H800q-40 0-68-28t-28-68v-192q0-40 28-68t68-28h192q40 0 68 28t28 68zm0-512v192q0 40-28 68t-68 28H800q-40 0-68-28t-28-68V736q0-40 28-68t68-28h192q40 0 68 28t28 68zm0-512v192q0 40-28 68t-68 28H800q-40 0-68-28t-28-68V224q0-40 28-68t68-28h192q40 0 68 28t28 68z" />
                                        </svg>
                                    </button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <Menu.Item onClick={navigateTo('https://headwayapp.co/pricewell-changelog')}>
                                        What's new?
                                    </Menu.Item>
                                    <Menu.Item onClick={handleShowApiKey}>API Key</Menu.Item>
                                    {pathname.startsWith(ROUTES.DASHBOARD) && (
                                        <Menu.Item onClick={handleManageSubscription}>Manage Subscription</Menu.Item>
                                    )}
                                    <Menu.Item onClick={handleSettings}>Settings</Menu.Item>
                                    <Menu.Item onClick={handleLogout}>Sign Out</Menu.Item>
                                </Menu.Dropdown>
                            </Menu>
                        </div>
                    </header>
                    <header className="w-full bg-sidebar py-5 px-6 z-10 overflow-y-scroll sm:hidden">
                        <div className="flex items-center justify-between">
                            <a
                                href="index.html"
                                className="text-white text-3xl font-semibold uppercase hover:text-gray-300"
                            >
                                <img src="/images/pricewell-logo-white.png" alt="PriceWell logo" className="h-10" />
                            </a>
                            <button className="text-white text-3xl focus:outline-none" onClick={handleClickMenu}>
                                {!isOpen && <img src="/images/bars.svg" alt="Click to open menu" className="w-3.5" />}
                                {isOpen && <img src="/images/times.svg" alt="Click to close menu" className="w-3.5" />}
                            </button>
                        </div>

                        {isOpen && (
                            <nav className="flex flex-col pt-4">
                                <div className="pb-2">
                                    <CreateButton currentPlan={currentPlan} showUpgradePopup={showUpgradePopup} />
                                </div>
                                <NavItems
                                    onClick={handleClickMenu}
                                    currentPlan={currentPlan}
                                    showUpgradePopup={showUpgradePopup}
                                />
                                <a
                                    href="https://headwayapp.co/pricewell-changelog"
                                    className="flex items-center text-white opacity-75 hover:opacity-100 py-4 pl-6 nav-item"
                                >
                                    What's new?
                                </a>
                                <button
                                    onClick={handleShowApiKey}
                                    className="flex items-center text-white opacity-75 hover:opacity-100 py-4 pl-6 nav-item"
                                >
                                    API Key
                                </button>
                                {pathname.startsWith(ROUTES.DASHBOARD) && (
                                    <button
                                        onClick={handleManageSubscription}
                                        className="flex items-center text-white opacity-75 hover:opacity-100 py-4 pl-6 nav-item"
                                    >
                                        Manage Subscription
                                    </button>
                                )}
                                <button
                                    onClick={handleSettings}
                                    className="flex items-center text-white opacity-75 hover:opacity-100 py-4 pl-6 nav-item"
                                >
                                    Settings
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="flex items-center text-white opacity-75 hover:opacity-100 py-4 pl-6 nav-item"
                                >
                                    Sign Out
                                </button>
                            </nav>
                        )}
                    </header>
                    <ReactModal isOpen={apiKeyModalOpen} style={MODAL_STYLES}>
                        <Modal
                            type="INFO"
                            title="API Key"
                            body={
                                <>
                                    <p className="pt-4">
                                        Use your API key to secure your Pricing Pages and Customer Portal.
                                    </p>
                                    <p className="pt-4">
                                        Check out our{' '}
                                        <a
                                            href="https://help.pricewell.com/features/customer_portal/secure-mode-advanced/"
                                            className="link"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Documentation
                                        </a>{' '}
                                        to learn more.
                                    </p>
                                    <div className="font-bold pt-4 pb-2">API Key:</div>
                                    <SnippetBox snippet={company?.apiKey || 'Loading...'} />
                                    <div className="font-bold pt-4 pb-2">Company Id:</div>
                                    <SnippetBox snippet={company?.id || 'Loading...'} />
                                </>
                            }
                            confirmButtonText="Done"
                            onConfirm={handleHideApiKey}
                        />
                    </ReactModal>
                </>
            )}
        </>
    );
}
